import React, { useEffect, useCallback, useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { SvgFilter } from '@devsbb/ui';

import { tk } from '@/i18n/translationKeys';
import { trackYourPaymentsPageVisitedEvent, TrackYourPaymentsEvents } from '@/analytics';
import { useScreenMatch } from '@/hooks/useScreenMatch';
import { useQueryParams } from '@/hooks/useQueryParams';
import { useApplicationData } from '@/providers/applicationData';
import { getLocale } from '@/utils/cookies';
import { PAY_NOW_OPTIONS_QUERY } from '@/gql/queries/payNowOptions';
import { LoadingUnit } from '@/components/Loading';
import { DisplayIf } from '@/components/DisplayIf';
import { Page } from '@/components/Page';
import { DocumentTitle, PageName } from '@/components/DocumentTitle';
import { Text } from '@/components/Text';
import { Wrapper } from '@/components/Wrapper';
import { ArrowLeftIcon } from '@/icons/ArrowLeftIcon';
import {
  PaymentState,
  PaymentEnum,
  PaymentsUnion,
  SubscriptionPayment,
  PayNowOptionsQuery,
} from '@/generated/types';
import { TopPane } from './TopPane';
import { LinkToPaymentMethods } from './LinkToPaymentMethods';
import { FilterPane } from './FilterPane';
import { PaymentsList } from './PaymentsList';
import { usePaymentsList, UsePaymentsListResult } from './PaymentsList/usePaymentsList';
import { FiltersScreen } from './FiltersScreen';
import { FailedBanner, WarningType } from './FailedBanner';
import {
  FailedBannerData,
  getFailedBannerData,
  hasOldInfraDebt,
} from './FailedBanner/getFailedBannerData';
import { FailedBannerPreloader } from './FailedBanner/FailedBannerPreloader';
import { FilterType } from './types';
import { PaymentInProgressModal } from './PaymentInProgressModal';

import { Root, BackButton, FilterButton } from './YourPaymentsPage.styles';
import { DEFAULT_LANGUAGE, PayNowStatus, QueryParamName } from '@/constants';
import { isInWebView } from '@/modules/webView';

const SHOW_FAILED_BANNER_WARNING_KEY = 'show-failed-banner-warning';
const PAYMENT_IN_PROGRESS_MODAL_DISMISSED_KEY = 'payment-in-progress-modal-dismissed';

const payNowStatusToWarningType: Record<PayNowStatus, WarningType> = {
  [PayNowStatus.success]: WarningType.payNowSuccess,
  [PayNowStatus.timeout]: WarningType.payNowTimeout,
};

const isPayNowStatus = (value: string | null): value is PayNowStatus =>
  Object.values(PayNowStatus).includes(value as PayNowStatus);

const ActualFailedBanner = ({
  loading,
  failedBannerData,
  warningType,
  isDismissed,
  onClose,
}: {
  loading: boolean;
  failedBannerData: FailedBannerData | null;
  warningType: WarningType | null;
  isDismissed: boolean;
  onClose: () => void;
}) => {
  if (isDismissed) {
    return null;
  }

  if (loading) {
    return <FailedBannerPreloader />;
  }

  if (failedBannerData) {
    return (
      <FailedBanner
        {...failedBannerData}
        variant={warningType ? 'warning' : 'error'}
        warningType={warningType ?? undefined}
        onClose={onClose}
      />
    );
  }
  return null;
};

const ActualFilterButton = ({
  paymentsListResult,
  filterValueCount,
  handleFilterClick,
}: {
  paymentsListResult: UsePaymentsListResult;
  filterValueCount: number;
  handleFilterClick: () => void;
}) => {
  const { t } = useTranslation();
  const queryParams = useQueryParams();
  const { subscriptionId } = queryParams;

  if (subscriptionId) {
    return null;
  }
  if (paymentsListResult.loading && !filterValueCount) {
    return <LoadingUnit width={21} height={8} />;
  }
  return (
    <FilterButton size="S" variant="Primary" onClick={handleFilterClick}>
      <SvgFilter />
      {filterValueCount
        ? `${t(tk.yourPaymentsFilterChipTitle)} (${filterValueCount})`
        : `${t(tk.yourPaymentsFilterChipTitle)}`}
    </FilterButton>
  );
};

const ActualFilter = ({
  filterValueCount,
  selectedFilters,
  setSelectedFilters,
  paymentsListResult,
  handleFilterClick,
}: {
  filterValueCount: number;
  selectedFilters: FilterType[];
  setSelectedFilters: React.Dispatch<React.SetStateAction<FilterType[]>>;
  paymentsListResult: UsePaymentsListResult;
  handleFilterClick: () => void;
}) => {
  const { flags } = useApplicationData();
  if (!flags.pmmp_filter_your_payments) {
    return null;
  }

  return (
    <>
      <DisplayIf desktop>
        <FilterPane
          filterValueCount={filterValueCount}
          selectedFilters={selectedFilters}
          setSelectedFilters={setSelectedFilters}
        />
      </DisplayIf>
      <DisplayIf mobile>
        <ActualFilterButton
          paymentsListResult={paymentsListResult}
          filterValueCount={filterValueCount}
          handleFilterClick={handleFilterClick}
        />
      </DisplayIf>
    </>
  );
};

export const YourPaymentsPage = (): JSX.Element => {
  const queryParams = useQueryParams();
  const { subscriptionId, device, status, paymentType } = queryParams;
  const selectedTypes: FilterType[] = Object.entries(queryParams)
    .filter(
      ([key, value]) =>
        Boolean(value) && key !== 'subscriptionId' && key !== QueryParamName.payNowStatus
    )
    .map(([key]) => key as FilterType);
  const [selectedFilters, setSelectedFilters] = useState<FilterType[]>(selectedTypes);
  const [showFiltersScreen, setShowFiltersScreen] = useState<boolean>(false);
  const [isFailedBannerDismissed, setIsFailedBannerDismissed] = useState<boolean>(false);
  const [showWarningBanner, setShowWarningBanner] = useState<boolean>(
    localStorage.getItem(SHOW_FAILED_BANNER_WARNING_KEY) === 'true'
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const shouldShowPaymentInProgressModal = !!searchParams.get(
    QueryParamName.showPaymentInProgressModal
  );
  const [payNowStatus] = useState<PayNowStatus | null>(() => {
    const value = searchParams.get(QueryParamName.payNowStatus);
    return isPayNowStatus(value) ? value : null;
  });

  const isPaymentInProgress: boolean =
    shouldShowPaymentInProgressModal &&
    localStorage.getItem(PAYMENT_IN_PROGRESS_MODAL_DISMISSED_KEY) !== 'true';
  const [showPaymentInProgressModal, setShowPaymentInProgressModal] =
    useState<boolean>(isPaymentInProgress);

  const { t } = useTranslation();
  const { isMobile } = useScreenMatch();
  const { userId, activeStore, storeCode, flags, routeBuilder } = useApplicationData();
  const navigate = useNavigate();
  const locale = getLocale() || DEFAULT_LANGUAGE;
  const theme = useTheme();

  const queryParamsFilterValues = [device, status, paymentType];

  const isFilterEmptyResult =
    (status === PaymentState.FAILED && paymentType === PaymentEnum.ORDER_PAYMENT) ||
    (status === 'CANCELED' && paymentType && paymentType !== PaymentEnum.ORDER_PAYMENT) ||
    (status === 'CANCELED' && device);

  const paymentsListResult = usePaymentsList({
    subscriptionId,
    device,
    status,
    paymentType,
    isFilterEmptyResult: !!isFilterEmptyResult,
  });

  const isPayNowEnabled = flags.pay_now_banner;

  const failedSubscriptionPaymentsListResult = usePaymentsList({
    status: PaymentState.FAILED,
    paymentType: PaymentEnum.SUBSCRIPTION_PAYMENT,
    isFilterEmptyResult: false,
    isForFailedBanner: true,
  });

  const failedSubscriptionPayments = useMemo(() => {
    const isSubscriptionPayment = (payment: PaymentsUnion): payment is SubscriptionPayment =>
      payment.__typename === 'SubscriptionPayment';
    return (failedSubscriptionPaymentsListResult.data?.payments ?? []).filter(
      isSubscriptionPayment
    );
  }, [failedSubscriptionPaymentsListResult.data]);

  // Old infra debt can only be settled through the retry flow, so pay now is only checked without it.
  const shouldCheckPayNowOptions =
    isPayNowEnabled &&
    !failedSubscriptionPaymentsListResult.loading &&
    !hasOldInfraDebt(failedSubscriptionPayments);

  const payNowOptionsResult = useQuery<PayNowOptionsQuery>(PAY_NOW_OPTIONS_QUERY, {
    skip: !shouldCheckPayNowOptions,
    fetchPolicy: 'network-only',
  });

  const failedBannerData = useMemo(
    () =>
      getFailedBannerData({
        failedPayments: failedSubscriptionPayments,
        failedTotalCount: failedSubscriptionPaymentsListResult.data?.totalCount ?? 0,
        failedTotalPrice: failedSubscriptionPaymentsListResult.data?.totalPrice ?? null,
        payNowOptions: payNowOptionsResult.data?.payNowOptions,
        isPayNowAvailable: shouldCheckPayNowOptions && !payNowOptionsResult.error,
        locale: getLocale(),
      }),
    [
      failedSubscriptionPayments,
      failedSubscriptionPaymentsListResult.data,
      payNowOptionsResult.data,
      payNowOptionsResult.error,
      shouldCheckPayNowOptions,
    ]
  );

  const isPayNowOptionsPending =
    shouldCheckPayNowOptions && !payNowOptionsResult.data && !payNowOptionsResult.error;
  const isFailedBannerLoading =
    failedSubscriptionPaymentsListResult.loading || isPayNowOptionsPending;

  const filterValueCount = queryParamsFilterValues.filter((value) => Boolean(value)).length;

  useEffect(() => {
    trackYourPaymentsPageVisitedEvent({
      path: location.pathname,
      store: storeCode,
      store_id: activeStore.store_id,
      locale,
    });
  }, []);

  const handleBackClick = useCallback(() => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      location.href = routeBuilder.buildYourProfileRoute();
    }
  }, [navigate, routeBuilder]);

  const handleFilterClick = () => {
    setShowFiltersScreen(true);
    TrackYourPaymentsEvents.filterChipClick({
      userId,
      store: storeCode,
    });
  };

  const handleClosePaymentInProgressModal = () => {
    localStorage.setItem(PAYMENT_IN_PROGRESS_MODAL_DISMISSED_KEY, 'true');
    localStorage.setItem(SHOW_FAILED_BANNER_WARNING_KEY, 'true');
    setShowWarningBanner(true);
    setShowPaymentInProgressModal(false);
  };

  // refresh won’t keep showing warning banner
  useEffect(() => {
    if (showWarningBanner) {
      localStorage.removeItem(SHOW_FAILED_BANNER_WARNING_KEY);
    }
  }, [showWarningBanner]);

  useEffect(() => {
    if (!searchParams.has(QueryParamName.payNowStatus)) {
      return;
    }
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete(QueryParamName.payNowStatus);
    setSearchParams(nextSearchParams, { replace: true });
  }, []);

  const warningType: WarningType | null = payNowStatus
    ? payNowStatusToWarningType[payNowStatus]
    : showWarningBanner
    ? WarningType.paymentInProgress
    : null;

  return showFiltersScreen && isMobile && flags.pmmp_filter_your_payments ? (
    <FiltersScreen
      totalCount={filterValueCount ? paymentsListResult?.data?.totalCount : null}
      paymentsListLoading={paymentsListResult?.loading}
      setShowFiltersScreen={setShowFiltersScreen}
    />
  ) : (
    <Page
      banner={
        <DisplayIf desktop>
          <ActualFailedBanner
            loading={isFailedBannerLoading}
            failedBannerData={failedBannerData}
            warningType={warningType}
            isDismissed={isFailedBannerDismissed}
            onClose={() => setIsFailedBannerDismissed(true)}
          />
        </DisplayIf>
      }
    >
      <DocumentTitle pageName={PageName.yourPayments} />
      <Root isMobile={isMobile}>
        <DisplayIf desktop>
          <Wrapper height="64px" justifyContent="space-between" margin="16px 0">
            <Wrapper>
              <Text
                typography="SmallHeadline"
                color={theme.colors.text.darkerGrey}
                data-testid="your-payments-page-main-title"
              >
                {t(tk.yourPaymentsPageMainTitle)}
              </Text>
            </Wrapper>

            <LinkToPaymentMethods />
          </Wrapper>
        </DisplayIf>

        <DisplayIf mobile>
          <Wrapper height="64px" justifyContent="space-between">
            <Wrapper>
              {isInWebView() ? null : (
                <BackButton onClick={handleBackClick} data-testid="back-button">
                  <ArrowLeftIcon />
                </BackButton>
              )}

              <Text
                typography="SuperTinyHeadline"
                color={theme.colors.text.darkerGrey}
                data-testid="your-payments-page-main-title"
              >
                {t(tk.yourPaymentsPageMainTitle)}
              </Text>
            </Wrapper>

            <LinkToPaymentMethods />
          </Wrapper>
          <ActualFailedBanner
            loading={isFailedBannerLoading}
            failedBannerData={failedBannerData}
            warningType={warningType}
            isDismissed={isFailedBannerDismissed}
            onClose={() => setIsFailedBannerDismissed(true)}
          />
        </DisplayIf>

        <TopPane />

        <ActualFilter
          filterValueCount={filterValueCount}
          selectedFilters={selectedFilters}
          setSelectedFilters={setSelectedFilters}
          paymentsListResult={paymentsListResult}
          handleFilterClick={handleFilterClick}
        />

        <PaymentsList
          paymentsListResult={paymentsListResult}
          filterValueCount={filterValueCount}
          setSelectedFilters={setSelectedFilters}
        />

        {showPaymentInProgressModal ? (
          <PaymentInProgressModal handleCloseModal={handleClosePaymentInProgressModal} />
        ) : null}
      </Root>
    </Page>
  );
};
